import { type EmbedSnippets, embedSnippets } from '@plato/embed/snippets';
import { ReadOnlyNote } from '../../components/action.tsx';
import { Loaded } from '../../components/page.tsx';
import { useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { type IntegrationSetup, isConnectedByEachUser } from '../../data/rows.ts';
import { AllowedOrigins, ApiKeys } from './keys.tsx';
import { CodeBlock, SnippetTabs } from './snippet-tabs.tsx';

export const VENDOR_GUIDE_URL = 'https://github.com/Ramora0/plato/blob/main/docs/vendor-guide.md';

const loadSetup = (data: ConsoleData, tenant: TenantSummary) =>
  Promise.all([data.getIntegrationSetup(tenant.id), data.listApiKeys(tenant.id)]);

export function EmbedPage() {
  const { query, reload } = useReloadableTenantQuery(loadSetup);
  const canEdit = useHasRole('admin');
  return (
    <>
      {!canEdit && <ReadOnlyNote needs="admin" />}
      <Loaded query={query}>
        {([setup, keys]) => {
          const snippets = snippetsFor(setup);
          return (
            <>
              <QuickStart setup={setup} />
              <AllowedOrigins
                key={setup.allowedOrigins.join()}
                origins={setup.allowedOrigins}
                canEdit={canEdit}
                onChanged={reload}
              />
              <ApiKeys keys={keys} canEdit={canEdit} onChanged={reload} />
              {snippets.ok ? (
                <Snippets snippets={snippets.value} />
              ) : (
                <p role="alert" className="notice notice-danger">
                  {snippets.message}
                </p>
              )}
            </>
          );
        }}
      </Loaded>
    </>
  );
}

type SnippetsResult =
  | { readonly ok: true; readonly value: EmbedSnippets }
  | { readonly ok: false; readonly message: string };

function snippetsFor(setup: IntegrationSetup): SnippetsResult {
  if (setup.publishableKey === null) {
    return {
      ok: false,
      message:
        'There is no active publishable key yet. An admin can create one under API keys above; the snippets appear here once it exists.',
    };
  }
  try {
    return {
      ok: true,
      value: embedSnippets({
        publishableKey: setup.publishableKey,
        apiOrigin: setup.apiOrigin,
        embedOrigin: setup.embedOrigin,
        allowedOrigins: setup.allowedOrigins,
        mcpServers: vendorServers(setup).map((server) => server.name),
      }),
    };
  } catch (cause) {
    return {
      ok: false,
      message: `The snippets could not be made from this tenant's setup: ${cause instanceof Error ? cause.message : String(cause)}`,
    };
  }
}

function vendorServers(setup: IntegrationSetup) {
  return setup.mcpServers.filter((server) => server.enabled && !isConnectedByEachUser(server));
}

function QuickStart({ setup }: { setup: IntegrationSetup }) {
  const steps = [
    {
      label: 'Your sites are registered, including localhost for development',
      done: setup.allowedOrigins.length > 0,
    },
    { label: 'A publishable key exists', done: setup.publishableKey !== null },
    { label: 'Install the two packages', done: null },
    { label: 'Put the assistant on the pages of signed-in users', done: null },
    { label: 'Add the endpoint that hands the browser a session', done: null },
    {
      label: 'Optional: connect your data through a tool server',
      done: vendorServers(setup).length > 0,
    },
  ];
  return (
    <section aria-labelledby="quick-start">
      <h2 id="quick-start">Quick start in 10 minutes</h2>
      <ol className="checklist">
        {steps.map((step) => (
          <li key={step.label}>
            <StepStatus done={step.done} />
            <span>{step.label}</span>
          </li>
        ))}
      </ol>
      <p className="muted">
        With test keys you see the assistant in your own app before connecting any data. The{' '}
        <a href={VENDOR_GUIDE_URL} target="_blank" rel="noreferrer">
          vendor guide
        </a>{' '}
        explains each step, the token your backend passes and the limits.
      </p>
    </section>
  );
}

function StepStatus({ done }: { done: boolean | null }) {
  if (done === null) return <span className="chip">You</span>;
  return done ? (
    <span className="chip chip-success">Done</span>
  ) : (
    <span className="chip chip-warning">To do</span>
  );
}

function Snippets({ snippets }: { snippets: EmbedSnippets }) {
  return (
    <>
      <section aria-labelledby="install">
        <h2 id="install">1. Install</h2>
        <CodeBlock snippet={snippets.install} />
      </section>
      <section aria-labelledby="frontend">
        <h2 id="frontend">2. Your pages</h2>
        <p className="muted">Pick one: the React component, the script tag or plain JavaScript.</p>
        <SnippetTabs label="Front end" snippets={snippets.frontend} />
      </section>
      <section aria-labelledby="backend">
        <h2 id="backend">3. Your backend</h2>
        <p className="muted">
          The secret key stays on your server. The endpoint runs behind your own sign-in and tells
          Plato who the user is.
        </p>
        <SnippetTabs label="Back end" snippets={snippets.backend} />
      </section>
      <section aria-labelledby="csp">
        <h2 id="csp">4. Content-Security-Policy</h2>
        <CodeBlock snippet={snippets.csp} />
      </section>
      <section aria-labelledby="notes">
        <h2 id="notes">Notes for this tenant</h2>
        <ul>
          {snippets.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </section>
    </>
  );
}
